import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { type Theme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getActiveLanguage, getTranslate } from 'react-localize-redux';
import { Link } from 'react-router-dom';

import { useAppSelector } from '../../custom-hooks/useAppSelector';
import { resolveContactSalesSignUpUrl } from '../../helpers/contactSalesSignUp';
import { accountRoutesConst } from '../../routes/account.routes';
import { customActions } from '../../services/datadogRUM/customActions';
import AppLessShell from '../AppLess/AppLessShell';

const PageContent = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 1132,
  margin: '0 auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 3, 10),
  },
}));

const IntroSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(6),
  },
}));

const PageHeading = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  fontSize: theme.typography.pxToRem(40),
  fontWeight: 600,
  lineHeight: '48px',
  letterSpacing: '-0.8px',
}));

const CardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'minmax(0, 1fr)',
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(3),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

const Card = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  [theme.breakpoints.up('md')]: {
    minHeight: 178,
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing(6),
  height: theme.spacing(6),
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
}));

const CardActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginTop: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
  },
}));

type CardConfig = {
  title: React.ReactNode;
  description: React.ReactNode;
  icon: React.ReactNode;
  action?: React.ReactNode;
  testId: string;
};

const getActionButtonSx = (theme: Theme) => ({
  width: '100%',
  textTransform: 'none',
  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
});

export const NoOrganisationAccess = () => {
  const email = useAppSelector((state) => state.account?.Email);
  const languageCode = useAppSelector((state) => getActiveLanguage(state.locale));
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const displayEmail = email || 'your email';
  const introMessage = translate('No_org_access_intro', { email: displayEmail }) as string;
  const [introPrefix = '', ...introSuffixParts] = introMessage.split(displayEmail);
  const introSuffix = introSuffixParts.join(displayEmail);

  useEffect(() => {
    datadogRum.addAction(customActions.NO_ORGANISATION_ACCESS_VIEWED);
  }, []);

  const handleContactSalesClick = async () => {
    const signUpUrl = await resolveContactSalesSignUpUrl(languageCode);

    datadogRum.addAction(customActions.NO_ORGANISATION_ACCESS_CONTACT_SALES_CLICKED, {
      signUpUrl,
    });
    window.location.assign(signUpUrl);
  };
  const handleLogoutClick = () => {
    datadogRum.addAction(customActions.NO_ORGANISATION_ACCESS_LOGOUT_CLICKED, {
      target: accountRoutesConst.Logout,
    });
  };
  const cards: CardConfig[] = [
    {
      title: translate('No_org_access_recently_joined_title'),
      description: translate('No_org_access_recently_joined_description'),
      icon: <MailOutlineIcon fontSize="small" />,
      action: (
        <Button
          color="primary"
          component={Link}
          data-fd="no-organisation-access-logout"
          onClick={handleLogoutClick}
          sx={getActionButtonSx}
          to={accountRoutesConst.Logout}
          variant="contained"
        >
          {translate('No_org_access_logout')}
        </Button>
      ),
      testId: 'no-organisation-access-card-recently-joined',
    },
    {
      title: translate('No_org_access_new_title'),
      description: translate('No_org_access_new_description'),
      icon: <DescriptionOutlinedIcon fontSize="small" />,
      action: (
        <Button
          color="primary"
          data-fd="no-organisation-access-contact-sales"
          onClick={handleContactSalesClick}
          sx={getActionButtonSx}
          type="button"
          variant="outlined"
        >
          {translate('No_org_access_contact_sales')}
        </Button>
      ),
      testId: 'no-organisation-access-card-new',
    },
    {
      title: translate('No_org_access_owner_title'),
      description: translate('No_org_access_owner_description'),
      icon: <LocalPhoneOutlinedIcon fontSize="small" />,
      testId: 'no-organisation-access-card-owner',
    },
    {
      title: translate('No_org_access_staff_title'),
      description: translate('No_org_access_staff_description'),
      icon: <PeopleOutlineIcon fontSize="small" />,
      testId: 'no-organisation-access-card-staff',
    },
  ];

  return (
    <AppLessShell contentLayout="page">
      <PageContent>
        <IntroSection>
          <PageHeading data-fd="no-organisation-access-heading" gutterBottom>
            {translate('No_org_access_heading')}
          </PageHeading>
          <Typography
            color="text.primary"
            data-fd="no-organisation-access-description"
            variant="body1"
          >
            {introPrefix}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {displayEmail}
            </Box>
            {introSuffix}
          </Typography>
        </IntroSection>
        <CardGrid>
          {cards.map(({ action, description, icon, testId, title }) => (
            <Card data-fd={testId} key={testId}>
              <CardContent>
                <IconWrapper>{icon}</IconWrapper>
                <Box>
                  <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h6">
                    {title}
                  </Typography>
                  <Typography color="text.secondary" variant="body1">
                    {description}
                  </Typography>
                </Box>
              </CardContent>
              {action ? <CardActions>{action}</CardActions> : null}
            </Card>
          ))}
        </CardGrid>
      </PageContent>
    </AppLessShell>
  );
};

export default NoOrganisationAccess;
